import { rememberOfflineDbName } from "./offlineIdentity";

const DB_VERSION = 1;
const STORE_VEHICLES = "vehicles";
const STORE_META = "meta";

function requestToPromise(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(tx) {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error || new Error("IndexedDB transaction aborted"));
    tx.onerror = () => reject(tx.error);
  });
}

export function isQuotaError(error) {
  const name = error?.name || "";
  const code = error?.code;
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22
  );
}

export function openOfflineDb(dbName) {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(dbName, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_VEHICLES)) {
        const vehicles = db.createObjectStore(STORE_VEHICLES, { keyPath: "id" });
        vehicles.createIndex("source", "source", { unique: false });
        vehicles.createIndex("vehicleSuffix4", "vehicleSuffix4", { unique: false });
        vehicles.createIndex("chassisSuffix6", "chassisSuffix6", { unique: false });
        vehicles.createIndex("customerNameLower", "customerNameLower", {
          unique: false,
        });
        vehicles.createIndex("loanLower", "loanLower", { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: "key" });
      }
    };
    request.onsuccess = () => {
      rememberOfflineDbName(dbName);
      resolve(request.result);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function putVehicleBatch(db, records) {
  if (!records.length) return;
  const tx = db.transaction(STORE_VEHICLES, "readwrite");
  const store = tx.objectStore(STORE_VEHICLES);
  for (const record of records) {
    if (!record?.id) continue;
    store.put(record);
  }
  await transactionDone(tx);
}

export async function getMeta(db, key) {
  const tx = db.transaction(STORE_META, "readonly");
  return requestToPromise(tx.objectStore(STORE_META).get(key));
}

export async function setMeta(db, value) {
  const tx = db.transaction(STORE_META, "readwrite");
  tx.objectStore(STORE_META).put(value);
  await transactionDone(tx);
}

export async function countVehicles(db) {
  const tx = db.transaction(STORE_VEHICLES, "readonly");
  return requestToPromise(tx.objectStore(STORE_VEHICLES).count());
}

export async function countVehiclesBySource(db, source) {
  const tx = db.transaction(STORE_VEHICLES, "readonly");
  const index = tx.objectStore(STORE_VEHICLES).index("source");
  return requestToPromise(index.count(IDBKeyRange.only(source)));
}

export async function getVehiclesByIndex(db, indexName, value) {
  if (!value) return [];
  const tx = db.transaction(STORE_VEHICLES, "readonly");
  const index = tx.objectStore(STORE_VEHICLES).index(indexName);
  return requestToPromise(index.getAll(value));
}

export async function getVehiclesByPrefix(db, indexName, prefix) {
  if (!prefix) return [];
  const upper = `${prefix}\uffff`;
  const tx = db.transaction(STORE_VEHICLES, "readonly");
  const index = tx.objectStore(STORE_VEHICLES).index(indexName);
  return requestToPromise(index.getAll(IDBKeyRange.bound(prefix, upper)));
}

export async function pruneVehiclesNotInSet(db, source, keepIds) {
  const tx = db.transaction(STORE_VEHICLES, "readwrite");
  const index = tx.objectStore(STORE_VEHICLES).index("source");
  await new Promise((resolve, reject) => {
    const request = index.openCursor(IDBKeyRange.only(source));
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      const cursor = request.result;
      if (!cursor) {
        resolve();
        return;
      }
      if (!keepIds.has(cursor.value.id)) {
        cursor.delete();
      }
      cursor.continue();
    };
  });
  await transactionDone(tx);
}

export function deleteOfflineDatabase(name) {
  if (!name) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve();
  });
}

export async function wipeAllOfflineDatabases(knownName = "") {
  const names = new Set();
  if (knownName) names.add(knownName);
  try {
    if (typeof window.indexedDB.databases === "function") {
      const list = await window.indexedDB.databases();
      (list || []).forEach((entry) => {
        const name = entry?.name || "";
        if (name.startsWith("fr-offline-v1-")) names.add(name);
      });
    }
  } catch {
    /* ignore */
  }
  await Promise.all([...names].map((name) => deleteOfflineDatabase(name)));
}
